import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ApiErrorSummary } from '@/components/ApiErrorSummary';
import { CreateUserForm } from '../components/CreateUserForm';
import { useUserStore } from '../store';
import { userService } from '../services';
import { getApiErrorDetails, type ApiErrorDetails } from '@/lib/getApiErrorMessage';
import type { CreateUserFormData } from '../schemas';

export function CreateUserPage() {
  usePageTitle('Create User');
  const navigate = useNavigate();
  const fetchUsers = useUserStore((state) => state.fetchUsers);
  const [submitError, setSubmitError] = useState<ApiErrorDetails | null>(null);

  const handleSubmit = async (data: CreateUserFormData) => {
    setSubmitError(null);
    try {
      await userService.createUser(data);
      toast.success('User created successfully.');
      fetchUsers();
      navigate('/administration/users');
    } catch (err) {
      const errorDetails = getApiErrorDetails(err, 'Failed to create user');
      setSubmitError(errorDetails);
      toast.error(errorDetails.message);
    }
  };

  const handleCancel = () => {
    navigate('/administration/users');
  };

  return (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleCancel}
        className="gap-1"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Users
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Create New User</CardTitle>
          <CardDescription>
            Add a new user to the system. Fill in all required fields.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ApiErrorSummary error={submitError} />
          <CreateUserForm onSubmit={handleSubmit} onCancel={handleCancel} />
        </CardContent>
      </Card>
    </div>
  );
}
